/**
 * Insurance Lead Queue Component
 * Main queue view with sortable table, filtering, and pagination
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  InsuranceLead,
  LeadStatus,
  LeadQueryParams,
  ManualEntryData,
} from '../../types/insurance';
import {
  getInsuranceLeads,
  claimLead,
  unclaimLead,
  completeLead,
  deleteLead,
  updateLead,
} from '../../services/insuranceApi';
import { useAuth } from '../../auth/AuthContext';
import { InsuranceLeadFilters } from './InsuranceLeadFilters';
import { InsuranceLeadDetail } from './InsuranceLeadDetail';
import { LeadStatusBadge } from './LeadStatusBadge';
import { MatchConfidenceBadge } from './MatchConfidenceBadge';

// Location mapping for filter dropdown
const LOCATIONS = [
  { id: 1, name: "Darling's Ford" },
  { id: 2, name: "Darling's Honda" },
  { id: 3, name: "Darling's Automall" },
  { id: 4, name: "Darling's Chrysler Dodge" },
  { id: 5, name: "Darling's Chevrolet" },
  { id: 6, name: "Darling's VW Audi" },
  { id: 7, name: "Green Light Used" },
  { id: 8, name: "Darling's Waterfront" },
  { id: 9, name: "Green Point" },
  { id: 10, name: "Darling's Kia" },
  { id: 11, name: "Darling's Nissan" },
  { id: 12, name: "Darling's Toyota" },
];

type SortField = 'customerName' | 'location' | 'status' | 'matchConfidence' | 'createdAt' | 'claimedBy';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 10;

interface InsuranceLeadQueueProps {
  isAdmin?: boolean;
}

export const InsuranceLeadQueue: React.FC<InsuranceLeadQueueProps> = ({
  isAdmin = false,
}) => {
  const { user } = useAuth();

  // Filter state
  const [status, setStatus] = useState<LeadStatus | 'all'>('all');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [myLeadsOnly, setMyLeadsOnly] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Data state
  const [leads, setLeads] = useState<InsuranceLead[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detail modal state
  const [selectedLead, setSelectedLead] = useState<InsuranceLead | null>(null);
  const [autoOpenComplete, setAutoOpenComplete] = useState(false);

  // Action loading state
  const [actionLeadId, setActionLeadId] = useState<string | null>(null);

  // Check if user is current user
  const isCurrentUser = useCallback(
    (userId?: string) => {
      return userId === user?.id;
    },
    [user?.id]
  );

  // Fetch leads
  const fetchLeads = useCallback(
    async (loadMore = false) => {
      if (loadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setLeads([]);
      }
      setError(null);

      try {
        const params: LeadQueryParams = {
          limit: 50,
        };

        if (status !== 'all') {
          params.status = status;
        }
        if (locationId) {
          params.locationId = locationId;
        }
        if (myLeadsOnly) {
          params.myLeadsOnly = true;
        }
        if (loadMore && nextToken) {
          params.nextToken = nextToken;
        }

        const response = await getInsuranceLeads(params);

        if (response.success && response.data) {
          if (loadMore) {
            setLeads((prev) => [...prev, ...response.data!.leads]);
          } else {
            setLeads(response.data.leads);
          }
          setNextToken(response.data.nextToken);
          setTotalCount(response.data.count);
        } else {
          setError(response.error || 'Failed to fetch leads');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch leads');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [status, locationId, myLeadsOnly, nextToken]
  );

  // Initial load and filter changes
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchLeads(false);
  }, [status, locationId, myLeadsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort leads
  const sortedLeads = useMemo(() => {
    const sorted = [...leads].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'customerName':
          comparison = (a.sourceData.customerName || '').localeCompare(
            b.sourceData.customerName || ''
          );
          break;
        case 'location':
          comparison = (a.locationName || '').localeCompare(b.locationName || '');
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'matchConfidence': {
          // Sort order: exact > partial > none
          const order = { exact: 0, partial: 1, none: 2 };
          comparison = (order[a.matchConfidence] || 2) - (order[b.matchConfidence] || 2);
          break;
        }
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'claimedBy':
          comparison = (a.claimedByName || '').localeCompare(b.claimedByName || '');
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [leads, sortField, sortDirection]);

  // Paginate sorted leads
  const totalPages = Math.ceil(sortedLeads.length / ITEMS_PER_PAGE);
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedLeads.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedLeads, currentPage]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    setCurrentPage(1); // Reset to first page when sorting changes
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort header component
  const SortHeader: React.FC<{ field: SortField; label: string; className?: string }> = ({
    field,
    label,
    className = '',
  }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="text-gray-400">
          {sortField === field ? (
            sortDirection === 'asc' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )
          ) : (
            <svg className="w-4 h-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          )}
        </span>
      </div>
    </th>
  );

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Handle claim
  const handleClaim = async (leadId: string) => {
    setActionLeadId(leadId);
    try {
      const response = await claimLead(leadId);
      if (response.success && response.data) {
        setLeads((prev) =>
          prev.map((l) => (l.leadId === leadId ? response.data! : l))
        );
        if (selectedLead?.leadId === leadId) {
          setSelectedLead(response.data);
        }
      } else {
        setError(response.error || 'Failed to claim lead');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to claim lead');
    } finally {
      setActionLeadId(null);
    }
  };

  // Handle unclaim
  const handleUnclaim = async (leadId: string) => {
    setActionLeadId(leadId);
    try {
      const response = await unclaimLead(leadId);
      if (response.success && response.data) {
        setLeads((prev) =>
          prev.map((l) => (l.leadId === leadId ? response.data! : l))
        );
        if (selectedLead?.leadId === leadId) {
          setSelectedLead(response.data);
        }
      } else {
        setError(response.error || 'Failed to release lead');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to release lead');
    } finally {
      setActionLeadId(null);
    }
  };

  // Handle complete
  const handleComplete = async (leadId: string, outcome: 'quoted_purchased' | 'quoted_not_purchased' | 'bad_lead', notes?: string) => {
    const response = await completeLead(leadId, outcome, notes);
    if (response.success && response.data) {
      setLeads((prev) =>
        prev.map((l) => (l.leadId === leadId ? response.data! : l))
      );
      setSelectedLead(null);
    } else {
      throw new Error(response.error || 'Failed to complete lead');
    }
  };

  // Handle delete (hard delete for leads added in error)
  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Are you sure you want to delete this lead? This cannot be undone.')) return;
    setActionLeadId(leadId);
    try {
      const response = await deleteLead(leadId);
      if (response.success) {
        setLeads((prev) => prev.filter((l) => l.leadId !== leadId));
        if (selectedLead?.leadId === leadId) setSelectedLead(null);
      } else {
        setError(response.error || 'Failed to delete lead');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete lead');
    } finally {
      setActionLeadId(null);
    }
  };

  // Handle manual entry update
  const handleUpdateManualEntry = async (leadId: string, data: ManualEntryData) => {
    const response = await updateLead(leadId, { manualEntry: data });
    if (response.success && response.data) {
      setLeads((prev) =>
        prev.map((l) => (l.leadId === leadId ? response.data! : l))
      );
      if (selectedLead?.leadId === leadId) {
        setSelectedLead(response.data);
      }
    } else {
      throw new Error(response.error || 'Failed to update lead');
    }
  };

  return (
    <div>
      {/* Filters */}
      <InsuranceLeadFilters
        status={status}
        locationId={locationId}
        myLeadsOnly={myLeadsOnly}
        onStatusChange={setStatus}
        onLocationChange={setLocationId}
        onMyLeadsOnlyChange={setMyLeadsOnly}
        locations={LOCATIONS}
      />

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchLeads(false);
            }}
            className="mt-2 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && leads.length === 0 && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {myLeadsOnly
              ? "You haven't claimed any leads yet."
              : 'No leads match your current filters.'}
          </p>
          {myLeadsOnly && (
            <button
              onClick={() => {
                setMyLeadsOnly(false);
                setStatus('unclaimed');
              }}
              className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              View available leads
            </button>
          )}
        </div>
      )}

      {/* Lead Table */}
      {!isLoading && leads.length > 0 && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader field="customerName" label="Customer" />
                    <SortHeader field="location" label="Location" />
                    <SortHeader field="status" label="Status" />
                    <SortHeader field="matchConfidence" label="Match" />
                    <SortHeader field="createdAt" label="Date/Time" />
                    <SortHeader field="claimedBy" label="Claimed By" />
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLeads.map((lead) => {
                    const isClaimedByMe = lead.claimedBy && isCurrentUser(lead.claimedBy);
                    const canUnclaim = lead.status === 'claimed' && isClaimedByMe;
                    const canComplete = lead.status === 'claimed' && isClaimedByMe;
                    const canDelete = ['unclaimed', 'needs_manual_review', 'claimed'].includes(lead.status);
                    const isActionLoading = actionLeadId === lead.leadId;

                    return (
                      <tr
                        key={lead.leadId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedLead(lead)}
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {lead.sourceData.customerName}
                          </div>
                          <div className="text-xs text-gray-500">
                            Stock #{lead.stockNumber}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{lead.locationName}</div>
                        </td>
                        <td className="px-4 py-3">
                          <LeadStatusBadge status={lead.status} />
                        </td>
                        <td className="px-4 py-3">
                          <MatchConfidenceBadge confidence={lead.matchConfidence} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {formatDate(lead.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">
                            {lead.claimedByName || (lead.claimedBy ? 'Unknown' : '-')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            {canComplete && (
                              <button
                                onClick={() => {
                                  setAutoOpenComplete(true);
                                  setSelectedLead(lead);
                                }}
                                className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                              >
                                Complete
                              </button>
                            )}
                            {canUnclaim && (
                              <button
                                onClick={() => handleUnclaim(lead.leadId)}
                                disabled={isActionLoading}
                                className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 disabled:bg-gray-100 rounded"
                              >
                                {isActionLoading ? '...' : 'Release'}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setAutoOpenComplete(false);
                                setSelectedLead(lead);
                              }}
                              className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                            >
                              View
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(lead.leadId)}
                                disabled={isActionLoading}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:text-gray-300 rounded"
                                title="Delete lead"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedLeads.length)} of {sortedLeads.length} leads
              {nextToken && ` (${totalCount}+ total)`}
            </p>
            <div className="flex items-center gap-2">
              {/* Load More button if there are more leads on the server */}
              {nextToken && (
                <button
                  onClick={() => fetchLeads(true)}
                  disabled={isLoadingMore}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 rounded-md"
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
              {/* Page navigation */}
              {totalPages > 1 && (
                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {/* Show page numbers - max 5 visible */}
                    {(() => {
                      const pages: (number | string)[] = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        if (currentPage <= 3) {
                          pages.push(1, 2, 3, 4, '...', totalPages);
                        } else if (currentPage >= totalPages - 2) {
                          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                        } else {
                          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                        }
                      }

                      return pages.map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ));
                    })()}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedLead && (
        <InsuranceLeadDetail
          lead={selectedLead}
          isOpen={true}
          onClose={() => {
            setSelectedLead(null);
            setAutoOpenComplete(false);
          }}
          onClaim={handleClaim}
          onUnclaim={handleUnclaim}
          onComplete={handleComplete}
          onUpdateManualEntry={handleUpdateManualEntry}
          isCurrentUser={isCurrentUser}
          autoOpenComplete={autoOpenComplete}
        />
      )}
    </div>
  );
};

export default InsuranceLeadQueue;
